/* =========================================================================
   Vidalite — Collection page
   /collections/<handle> renders main-collection.liquid which includes the
   shared collection-grid snippet (filter pill + drawer + auto-fill grid).

   Same vocabulary as the catalog hub:
     - Click a shape tile (configurator mode) → narrows the grid
     - Click the Filter pill → opens left-side drawer with finish family /
       finish / shape / price multi-select pills
     - Drawer pills toggle on/off; price has min/max numeric inputs
     - Clear all + Show results in the drawer foot
   ========================================================================= */

document.addEventListener('DOMContentLoaded', () => {

    /* Mobile menu toggle is handled globally in v3-main.js (event-delegated).
       The duplicate handler that used to live here double-toggled the drawer
       on collection pages, so it opened then immediately closed. Removed. */

    /* --- DOM refs ------------------------------------------------------ */
    const cards       = Array.from(document.querySelectorAll('.cat-card'));
    const empty       = document.getElementById('catEmpty');
    const gridResults = document.querySelector('[data-grid-results]');

    if (!cards.length) return;


    /* --- Shape picker (configurator mode only) -------------------------
       Two click behaviours layered onto each shape tile:
         1. Clicking a finish swatch / "+N" link / any inner anchor → that
            anchor's native href takes over (product page, grid jump, etc.)
         2. Clicking anywhere else on the tile → navigate to the shape's
            product page (data-shape-url), so the whole card behaves like
            a single browseable link.
       Filter state is also updated so if the user lands back here, the
       grid below is pre-filtered to the most-recently-viewed shape. */
    const state = { shape: 'all' };

    const shapeButtons = document.querySelectorAll('.cat-shape');

    // Multi-product shapes (e.g. Pendant) can't deep-link to one PDP — narrow
    // the in-page grid to the shape and scroll down to it so the user sees the
    // whole line-up instead of a single hero product.
    function filterToShape(tile) {
        state.shape = tile.dataset.shape || 'all';
        shapeButtons.forEach(b => b.classList.toggle('cat-shape--active', b === tile));
        applyFilters();
        const grid = document.getElementById('full-grid');
        if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    shapeButtons.forEach(tile => {
        tile.addEventListener('click', (e) => {
            // If the click originated on an inner anchor (swatch, +N, CTA,
            // cover image, title link), let the browser follow that link.
            if (e.target.closest('a')) {
                state.shape = tile.dataset.shape || 'all';
                shapeButtons.forEach(b => b.classList.toggle('cat-shape--active', b === tile));
                applyFilters();
                return;
            }
            // Single-product shape → navigate the whole tile to its PDP.
            // Multi-product shape (url === '#full-grid') → filter + scroll.
            const url = tile.dataset.shapeUrl;
            if (url && url !== '#full-grid') {
                window.location.href = url;
            } else {
                filterToShape(tile);
            }
        });
        // Keyboard accessibility — Enter / Space activates the tile.
        tile.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const url = tile.dataset.shapeUrl;
                if (url && url !== '#full-grid') window.location.href = url;
                else filterToShape(tile);
            }
        });
    });

    // "+N" pills — clicking should also filter the grid by the shape so
    // the user lands on a grid pre-narrowed to that shape's variants.
    document.querySelectorAll('[data-shape-jump]').forEach(more => {
        more.addEventListener('click', () => {
            state.shape = more.dataset.shapeJump || 'all';
            const tile = more.closest('.cat-shape');
            shapeButtons.forEach(b => b.classList.toggle('cat-shape--active', b === tile));
            applyFilters();
        });
    });

    // Browse-mode toggle — visual active state on the two pills above
    // the section title. The actual scroll is the anchor's default
    // behaviour, but we mark the pill so the user sees what they picked.
    const browseToggleBtns = document.querySelectorAll('.cat-browse-toggle__btn');
    browseToggleBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            browseToggleBtns.forEach(b => b.classList.toggle('cat-browse-toggle__btn--active', b === btn));
        });
    });


    /* --- Filter drawer (multi-select pills) ---------------------------- */
    const filterDrawer   = document.querySelector('[data-filter-drawer]');
    const filterBackdrop = document.querySelector('[data-filter-backdrop]');
    const filterToggle   = document.querySelector('[data-filter-toggle]');
    const filterCount    = document.querySelector('[data-filter-count]');
    const priceMinInput  = document.querySelector('[data-price-min]');
    const priceMaxInput  = document.querySelector('[data-price-max]');

    const drawerFilters = {
        family:   new Set(),
        finish:   new Set(),
        shape:    new Set(),
        priceMin: null,
        priceMax: null
    };

    const FILTER_LABELS = {
        family: {
            'classic':       'Plated Metal',
            'premium-metal': 'Premium Metal',
            'glass':         'Glass',
            'matte-color':   'Matte Color',
            'matte':         'Sand Matte'
        },
        finish: {},
        shape: {
            'spot':            'Spot Light',
            '2head':           '2-Head Track',
            '3head':           '3-Head Track',
            '4head':           '4-Head Track',
            '5head':           '5-Head Track',
            '6head':           '6-Head Track',
            'round-trim':      'Round Trim',
            'round-trimless':  'Round Trimless',
            'round-gimbal':    'Round Gimbal',
            'square-trim':     'Square Trim',
            'square-trimless': 'Square Trimless'
        }
    };

    function prettyFromHandle(handle) {
        return handle.replace(/-/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());
    }

    function buildFilterOptions() {
        ['family', 'finish', 'shape'].forEach(group => {
            const list = document.querySelector(`[data-filter-options="${group}"]`);
            if (!list) return;
            const counts = new Map();
            // shape is single-valued per product; family/finish are space-separated
            // lists across the product's variants. Split before counting so each
            // value gets its own pill and a card contributes once per token.
            const isMulti = (group === 'family' || group === 'finish');
            cards.forEach(card => {
                const raw = card.dataset[group];
                if (!raw) return;
                const keys = isMulti ? raw.split(' ').filter(Boolean) : [raw];
                const seen = new Set();
                keys.forEach(key => {
                    if (seen.has(key)) return;
                    seen.add(key);
                    counts.set(key, (counts.get(key) || 0) + 1);
                });
            });
            const sorted = Array.from(counts.entries()).sort((a, b) => {
                const la = (FILTER_LABELS[group][a[0]] || prettyFromHandle(a[0])).toLowerCase();
                const lb = (FILTER_LABELS[group][b[0]] || prettyFromHandle(b[0])).toLowerCase();
                return la.localeCompare(lb);
            });
            list.innerHTML = '';
            sorted.forEach(([key, n]) => {
                const li = document.createElement('li');
                const label = FILTER_LABELS[group][key] || prettyFromHandle(key);
                // Finish pills include a color swatch tied to the CSS variable
                // --finish-{handle}. Other groups (family/shape) stay text-only.
                const swatch = group === 'finish'
                    ? `<span class="cat-filter-pill__swatch" style="background: var(--finish-${key}, #c8c4bb);"></span>`
                    : '';
                li.innerHTML = `
                    <button type="button" class="cat-filter-pill" data-filter-input="${group}" data-value="${key}" aria-pressed="false">
                        ${swatch}
                        <span class="cat-filter-pill__label">${label}</span>
                        <span class="cat-filter-pill__count">${n}</span>
                    </button>`;
                list.appendChild(li);
            });
        });
    }

    function activeFilterCount() {
        let n = drawerFilters.family.size + drawerFilters.finish.size + drawerFilters.shape.size;
        if (drawerFilters.priceMin != null) n++;
        if (drawerFilters.priceMax != null) n++;
        return n;
    }

    function refreshFilterCountBadge() {
        if (!filterCount || !filterToggle) return;
        const n = activeFilterCount();
        if (n === 0) {
            filterCount.hidden = true;
            filterCount.textContent = '0';
            filterToggle.classList.remove('cat-filter-launcher--active');
        } else {
            filterCount.hidden = false;
            filterCount.textContent = String(n);
            filterToggle.classList.add('cat-filter-launcher--active');
        }
    }

    let drawerReleaseTrap = null;

    function openDrawer() {
        if (!filterDrawer) return;
        filterDrawer.hidden = false;
        filterBackdrop.hidden = false;
        filterDrawer.offsetHeight; // reflow so the transform transitions
        filterDrawer.dataset.open = 'true';
        filterBackdrop.dataset.open = 'true';
        filterDrawer.setAttribute('aria-hidden', 'false');
        filterToggle?.setAttribute('aria-expanded', 'true');
        document.body.classList.add('cat-filter-open');
        if (window.VidaliteA11y) {
            drawerReleaseTrap = window.VidaliteA11y.trapFocus(filterDrawer, {
                onEscape: closeDrawer,
                restoreEl: filterToggle,
            });
        }
    }

    function closeDrawer() {
        if (!filterDrawer) return;
        filterDrawer.dataset.open = 'false';
        filterBackdrop.dataset.open = 'false';
        filterDrawer.setAttribute('aria-hidden', 'true');
        filterToggle?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('cat-filter-open');
        if (drawerReleaseTrap) { drawerReleaseTrap(); drawerReleaseTrap = null; }
        setTimeout(() => {
            if (filterDrawer.dataset.open === 'false') {
                filterDrawer.hidden = true;
                filterBackdrop.hidden = true;
            }
        }, 320);
    }

    function clearAllDrawerFilters() {
        drawerFilters.family.clear();
        drawerFilters.finish.clear();
        drawerFilters.shape.clear();
        drawerFilters.priceMin = null;
        drawerFilters.priceMax = null;
        document.querySelectorAll('[data-filter-input]').forEach(pill => pill.setAttribute('aria-pressed', 'false'));
        if (priceMinInput) priceMinInput.value = '';
        if (priceMaxInput) priceMaxInput.value = '';
        refreshFilterCountBadge();
        applyFilters();
    }


    /* --- Filtering ---------------------------------------------------- */
    function applyFilters() {
        let visible = 0;
        const famSet  = drawerFilters.family;
        const finSet  = drawerFilters.finish;
        const shpSet  = drawerFilters.shape;
        const lo      = drawerFilters.priceMin;
        const hi      = drawerFilters.priceMax;

        // Cards carry space-separated lists of every finish/family they
        // cover (one card per product, many variants). A card matches if
        // ANY of its tokens intersects with the active filter set.
        const anyIn = (csv, set) => (csv || '').split(' ').some(v => v && set.has(v));

        cards.forEach(card => {
            let matches = state.shape === 'all' || state.shape === card.dataset.shape;
            if (matches && famSet.size > 0 && !anyIn(card.dataset.family, famSet)) matches = false;
            if (matches && finSet.size > 0 && !anyIn(card.dataset.finish, finSet)) matches = false;
            if (matches && shpSet.size > 0 && !shpSet.has(card.dataset.shape))     matches = false;
            if (matches && (lo != null || hi != null)) {
                const price = parseFloat(card.dataset.price);
                if (isFinite(price)) {
                    if (lo != null && price < lo) matches = false;
                    if (matches && hi != null && price > hi) matches = false;
                }
            }
            card.classList.toggle('cat-card--hidden', !matches);
            if (matches) visible++;
        });

        if (empty) empty.hidden = visible !== 0;
        if (gridResults) {
            gridResults.textContent = visible === 1 ? '1 product' : `${visible} products`;
        }
    }


    /* --- Drawer wiring ------------------------------------------------- */
    if (filterDrawer) {
        buildFilterOptions();

        filterToggle?.addEventListener('click', () => {
            const open = filterDrawer.dataset.open === 'true';
            if (open) closeDrawer(); else openDrawer();
        });
        filterBackdrop?.addEventListener('click', closeDrawer);
        document.querySelectorAll('[data-filter-close]').forEach(el => {
            el.addEventListener('click', closeDrawer);
        });
        document.addEventListener('keydown', e => {
            if (e.key === 'Escape' && filterDrawer.dataset.open === 'true') closeDrawer();
        });
        document.querySelector('[data-filter-clear]')?.addEventListener('click', clearAllDrawerFilters);

        document.querySelectorAll('[data-filter-input]').forEach(pill => {
            pill.addEventListener('click', () => {
                const group = pill.dataset.filterInput;
                const val   = pill.dataset.value;
                const wasPressed = pill.getAttribute('aria-pressed') === 'true';
                if (wasPressed) {
                    pill.setAttribute('aria-pressed', 'false');
                    drawerFilters[group].delete(val);
                } else {
                    pill.setAttribute('aria-pressed', 'true');
                    drawerFilters[group].add(val);
                }
                refreshFilterCountBadge();
                applyFilters();
            });
        });

        const onPrice = () => {
            const lo = parseFloat(priceMinInput?.value);
            const hi = parseFloat(priceMaxInput?.value);
            drawerFilters.priceMin = isFinite(lo) && lo >= 0 ? lo : null;
            drawerFilters.priceMax = isFinite(hi) && hi >= 0 ? hi : null;
            refreshFilterCountBadge();
            applyFilters();
        };
        priceMinInput?.addEventListener('input', onPrice);
        priceMaxInput?.addEventListener('input', onPrice);
    }


    /* --- Initial render ------------------------------------------------ */
    applyFilters();
});


/* Collection description: collapsed by default with a "Read more" toggle.
   Standalone so it runs on every collection page, including those without
   the filter grid above. */
(function () {
    function init() {
        const sub = document.querySelector('[data-coll-desc]');
        const btn = document.querySelector('[data-coll-toggle]');
        if (!sub || !btn) return;
        if (sub.querySelectorAll('p').length <= 1) { btn.hidden = true; return; } // only the lede — nothing to expand
        btn.hidden = false;
        btn.addEventListener('click', function () {
            const expanded = sub.classList.toggle('is-expanded');
            btn.textContent = expanded ? 'Read less' : 'Read more';
            btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();
